import { AfterViewInit, ChangeDetectorRef, Component, Input, OnInit, ViewChild } from '@angular/core';
import { ChannelSearchDialogComponent } from '@components/channel-search-dialog/channel-search-dialog.component';
import { ChannelService } from '@service/channel/channel.service';
import { Channel } from 'icreate/common/models/channel';
import { lastValueFrom, take } from 'rxjs';
import { ChannelType, getAnalogTypesSet, getDigitalTypesSet, isDigital } from 'icreate/common/models/channel-type';
import { ElementSettings } from 'icreate/common/models/editor-viewer-element';
import * as _ from 'lodash';
import { ElementAttribute } from '@models/editor';
import { Vessel } from 'icreate/common/models/vessel';
import { CanvasInputMode } from '@service/canvas/canvas-input-mode.service';
import { AutosavePage } from '@service/auto-save-controller.service';
import { EditorService } from '@service/editor.service';
import { ElementIntegrationViewData } from 'icreate/common/models/element-integration';
import { ElementSettingsService } from '@service/canvas/element-settings.service';
import { GaugeExtensionService } from '@service/canvas/gauge-extension.service';
import { SystemColor } from 'icreate/common/models/system-color';
import { SystemColorService } from '@service/system-color.service';
import { EditorMimicActionsService } from '@service/editor-mimic-actions.service';
import { MimicsService } from '@service/mimics.service';
import { MatDialog } from '@angular/material/dialog';
import { GaugeElementSettingsComponent } from '@components/editor/gauge-element-settings/gauge-element-settings.component';

@Component({
    selector: 'app-default-element-settings',
    templateUrl: './default-element-settings.component.html',
    styleUrl: './default-element-settings.component.css',
    standalone: false,
})
export class DefaultElementSettingsComponent implements OnInit, AfterViewInit {
    @Input() selectedElement;
    @Input() mimicId: string;
    @Input() vessel: Vessel;
    @Input() userCanEdit: boolean;
    @Input() elementSettings: ElementSettings;
    @Input() elementIntegration: ElementIntegrationViewData;
    @ViewChild('gaugeSettings') gaugeSettings: GaugeElementSettingsComponent;

    assignedChannel: Channel;
    initialChannel: Channel;
    isLoadingChannel: boolean = false;
    setShouldDisableTypes: boolean = false;
    allowedTypesSet: Set<ChannelType>;
    initialSetting: ElementSettings;
    initialElement;

    systemColors: SystemColor[];

    constructor(
        private cdr: ChangeDetectorRef,
        private dialog: MatDialog,
        private channelService: ChannelService,
        private editorService: EditorService,
        private elementSettingsService: ElementSettingsService,
        private gaugeService: GaugeExtensionService,
        private systemColorService: SystemColorService,
        private editorMimicActionsService: EditorMimicActionsService,
        private mimicService: MimicsService
    ) {}

    async ngOnInit(): Promise<void> {
        if (!this.elementSettings) {
            this.elementSettings = {
                id: this.elementSettings?.id,
                channelTag: null,
                channelIsDigital: false,
                elementIntegrationId: this.elementIntegration?.id ?? null,
                elementId: this.selectedElementId,
                mimicId: this.mimicId,
                ranges: [],
            };
        }
        this.initialSetting = _.cloneDeep(this.elementSettings);
        this.isLoadingChannel = true;
        this.updateAllowedChannelTypes();
        this.systemColors = await this.systemColorService.getAll(this.vessel.id);
        const channelTag = this.elementSettings?.channelTag;
        if (channelTag) {
            const channel = await this.channelService.getByRowId(this.vessel.id, channelTag);
            if (channel) {
                this.assignedChannel = channel;
                this.initialChannel = structuredClone(this.assignedChannel);
            }
        }
        this.isLoadingChannel = false;
    }

    ngAfterViewInit(): void {
        this.initialElement = this.selectedElement.cloneNode(true);
    }

    async openChannelSelector() {
        if (!this.userCanEdit) return;
        const dialogRef = this.dialog.open(ChannelSearchDialogComponent, {
            data: {
                vesselId: this.vessel?.id,
                assignedChannel: this.assignedChannel,
                allowedTypesSet: this.allowedTypesSet,
                setShouldDisableTypes: this.setShouldDisableTypes,
                multipleSelection: false,
                preSelectedChannelsCount: 0,
                stopAutoSave: true,
                autoSavePage: AutosavePage.MIMIC,
            },
        });
        const channelSelected: Channel = await lastValueFrom(dialogRef.afterClosed());
        if (channelSelected) {
            this.assignedChannel = undefined;
            this.isLoadingChannel = true;
            const channel: Channel = await this.channelService.getByRowId(this.vessel.id, channelSelected.rowId);

            if (channel) {
                const elementType = this.getElementType();
                this.assignedChannel = channel;
                this.elementSettings = {
                    ...this.elementSettings,
                    id: this.elementSettings?.id,
                    channelTag: this.assignedChannel?.rowId ?? '',
                    channelIsDigital: isDigital(this.assignedChannel?.fcuData?.channelType),
                    elementIntegrationId: this.elementIntegration?.id ?? null,
                    elementId: this.selectedElementId,
                    mimicId: this.mimicId,
                    ranges: [],
                };
                if (elementType === CanvasInputMode.GAUGE) {
                    this.gaugeService.setChannel(channel, this.selectedElement, this.systemColors);
                }
            }
            this.isLoadingChannel = false;
        }
    }

    removeAssignedChannel() {
        this.assignedChannel = undefined;
        this.elementSettings = {
            ...this.elementSettings,
            channelTag: '',
            channelIsDigital: false,
        };
        const elementType = this.getElementType();

        if (elementType === CanvasInputMode.GAUGE) {
            this.gaugeService.removeChannel(this.selectedElement);
        }
    }

    getElementType() {
        return this.selectedElement?.getAttribute(ElementAttribute.iCreateType);
    }

    hasChanges(): boolean {
        if (this.isGaugeElement && this.gaugeSettings?.hasChanges) return true;
        return !_.isEqual(this.initialSetting, this.elementSettings);
    }

    async save(): Promise<boolean> {
        this.editorService.showLoadingSpinner(true);
        try {
            if (!this.elementSettings?.id) {
                const { newId } = await this.elementSettingsService.create(this.elementSettings);
                this.elementSettings.id = newId;
            } else {
                await this.elementSettingsService.update(this.elementSettings);
            }

            if (
                this.isGaugeElement &&
                (!_.isEqual(this.initialChannel, this.assignedChannel) || this.gaugeSettings?.hasChanges)
            ) {
                const currentSvgContent = this.editorMimicActionsService.serializeCurrentSvg();
                const currentMimic = this.editorService.getCurrentMimic();
                currentMimic.svgcontent = currentSvgContent;
                await this.mimicService.update(currentMimic, true);
                this.editorService.updateMimicContent(currentSvgContent);
                this.gaugeSettings?.updateInitialElement(this.selectedElement);
                this.initialElement = this.selectedElement.cloneNode(true);
            }
            this.initialSetting = _.cloneDeep(this.elementSettings);
            this.initialChannel = structuredClone(this.assignedChannel);
            return true;
        } catch (err) {
            console.error(err);
            throw Error();
        } finally {
            this.editorService.showLoadingSpinner(false);
        }
    }

    discardSettings() {
        if (this.isGaugeElement) {
            if (this.initialSetting.channelTag != this.elementSettings.channelTag) {
                if (!this.initialSetting.channelTag) {
                    this.gaugeService.removeChannel(this.selectedElement);
                } else {
                    this.gaugeService.setChannel(this.initialChannel, this.selectedElement, this.systemColors);
                }
            }
            this.elementSettings = _.cloneDeep(this.initialSetting);
            this.assignedChannel = _.cloneDeep(this.initialChannel);
            const svgcontent = document.getElementById('svgcontent');
            const layerContent = svgcontent.querySelector('.layer');
            layerContent.replaceChild(this.initialElement, this.selectedElement);
            this.selectedElement = this.initialElement.cloneNode(true);
        }
    }

    get selectedElementId() {
        return this.selectedElement ? this.selectedElement.getAttribute(ElementAttribute.id) : '';
    }

    get isGaugeElement() {
        if (!this.isComponent) return false;
        return this.selectedElement.getAttribute(ElementAttribute.iCreateType) == CanvasInputMode.GAUGE;
    }

    get isMotorElement() {
        const elementType = this.getElementType();
        return elementType === CanvasInputMode.DEFAULT_PUMP || elementType === CanvasInputMode.DEFAULT_FAN;
    }

    get isComponent() {
        if (!this.selectedElement) return false;
        return this.selectedElement.getAttribute(ElementAttribute.iCreateType);
    }

    private updateAllowedChannelTypes() {
        const elementType = this.getElementType();
        if (elementType === CanvasInputMode.GAUGE) {
            this.allowedTypesSet = getAnalogTypesSet();
            this.setShouldDisableTypes = true;
        } else if (this.isMotorElement) {
            this.allowedTypesSet = getDigitalTypesSet();
            this.setShouldDisableTypes = true;
        } else {
            this.allowedTypesSet = undefined;
            this.setShouldDisableTypes = false;
        }
    }
}
